import { memo, useState } from "react";
import { PrivilegeBenefit } from "../../services/EmployeeService";
import { createEvent } from "../../services/EventService";
import "./BenefitCard.css";

interface BenefitCardProps {
  benefit: PrivilegeBenefit;
  showUseButton?: boolean;
  pass_owner_id?: number;
  compagnie_id?: number;
  employee_scanning_id?: number | null;
  onEventCreated?: () => void;
}

const BenefitCard = ({
  benefit,
  showUseButton = false,
  pass_owner_id,
  compagnie_id,
  employee_scanning_id,
  onEventCreated,
}: BenefitCardProps) => {
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isSuccessMode, setIsSuccessMode] = useState(false);
  const [isErrorMode, setIsErrorMode] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const formatValue = (value: number | undefined) => {
    if (value === undefined || value === null) return "";
    const formatted = parseFloat(value.toString()).toString();
    return formatted.replace(/\.0+$/, "");
  };

  const handleUsePrivilege = async () => {
    if (!pass_owner_id || !compagnie_id) {
      setError("Missing required information to use privilege");
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const response = await createEvent({
        timestamp: new Date().toISOString(),
        pass_owner_id,
        compagnie_id,
        benefit_id: benefit.id,
        employee_scanning_id: employee_scanning_id ?? null,
      });

      if (onEventCreated) {
        onEventCreated();
      }

      // Update popup to show success message
      setSuccessMessage(
        response.record.message || "Privilege Activated Successfully!"
      );
      setIsSuccessMode(true);
    } catch (err: any) {
      console.error("Error creating event:", err);
      let extractedMessage = "Failed to use privilege. Please try again.";
      
      if (err.response?.data?.message) {
        if (typeof err.response.data.message === 'string') {
          extractedMessage = err.response.data.message;
        } else if (err.response.data.message.error?.response?.message) {
          extractedMessage = err.response.data.message.error.response.message;
        } else if (err.response.data.message.message) {
          extractedMessage = err.response.data.message.message;
          if (extractedMessage.startsWith("Error while creating event: ")) {
            extractedMessage = extractedMessage.replace("Error while creating event: ", "");
          }
        }
      }
      
      setErrorMessage(extractedMessage);
      setIsErrorMode(true);
    } finally {
      setIsCreating(false);
    }
  };

  const handleButtonClick = () => {
    setShowConfirmPopup(true);
    setIsClosing(false);
    setIsSuccessMode(false);
    setIsErrorMode(false);
    setSuccessMessage("");
    setErrorMessage("");
    setError(null);
  };

  const handleClosePopup = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowConfirmPopup(false);
      setIsClosing(false);
    }, 300); // Match animation duration
  };

  const handleCancel = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (!isClosing) {
      handleClosePopup();
    }
  };

  const handleCloseSuccessPopup = () => {
    handleClosePopup();
    setIsSuccessMode(false);
    setSuccessMessage("");
  };

  const handleCloseErrorPopup = () => {
    handleClosePopup();
    setIsErrorMode(false);
    setErrorMessage("");
  };

  return (
    <div className="benefit-card">
      {benefit.flash_banner_text && (
        <div className="benefit-card-banner">{benefit.flash_banner_text}</div>
      )}

      <div className="benefit-card-header">
        <h3 className="benefit-card-name">{benefit.name}</h3>

        {(benefit.value || (benefit.type === "gift" && !benefit.value)) && (
          <div className="benefit-card-value-container">
            <img
              src="/Rectangle .svg"
              alt="Value background"
              className="benefit-card-value-bg"
            />
            <span className="benefit-card-value-text">
              {benefit.type === "gift"
                ? benefit.gift_content || "free"
                : benefit.value
                ? benefit.type === "discount"
                  ? `-${formatValue(benefit.value)}%`
                  : benefit.type === "voucher"
                  ? `-${formatValue(benefit.value)}`
                  : formatValue(benefit.value)
                : ""}
            </span>
          </div>
        )}
      </div>

      {benefit.description && (
        <p className="benefit-card-description">{benefit.description}</p>
      )}

      {benefit.conditions && (
        <div className="benefit-card-conditions">
          <span className="benefit-card-conditions-label">Conditions:</span>

          {(benefit.start_date || benefit.end_date) && (
            <div className="benefit-card-dates">
              <img
                src="/calendar.svg"
                alt="Calendar"
                className="benefit-card-calendar-icon"
              />
              <span className="benefit-card-dates-text">
                {benefit.start_date &&
                  new Date(benefit.start_date).toLocaleDateString()}
                {benefit.start_date && benefit.end_date && " - "}
                {benefit.end_date &&
                  new Date(benefit.end_date).toLocaleDateString()}
              </span>
            </div>
          )}

          <span className="benefit-card-conditions-text">
            {benefit.conditions}
          </span>
        </div>
      )}

      {(benefit.type === "discount" ||
        benefit.type === "voucher" ||
        benefit.type === "gift") && (
        <img
          src={benefit.type === "gift" ? "/gift.svg" : "/voucher.svg"}
          alt={benefit.type === "gift" ? "Gift" : "Voucher"}
          className="benefit-card-type-icon"
        />
      )}

      {showUseButton && (
        <>
          <button
            className="benefit-card-use-button"
            onClick={handleButtonClick}
            disabled={isCreating || !pass_owner_id || !compagnie_id}
          >
            {isCreating ? "Processing..." : "Use privilege"}
          </button>
          {error && (
            <div
              className="benefit-card-error"
              style={{ color: "red", marginTop: "8px", fontSize: "12px" }}
            >
              {error}
            </div>
          )}
        </>
      )}

      {showConfirmPopup && (
        <>
          <div
            className={`benefit-card-popup-overlay ${
              isClosing ? "closing" : ""
            }`}
            onClick={(e) => {
              if (!isClosing && !isSuccessMode && !isErrorMode) {
                handleCancel(e);
              }
            }}
          ></div>
          <div
            className={`benefit-card-confirm-popup ${
              isClosing ? "closing" : ""
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="benefit-card-popup-content">
              {isSuccessMode ? (
                <>
                  <h3 className="benefit-card-popup-title">{successMessage}</h3>
                  <p className="benefit-card-popup-message">
                    The privilege has been successfully
                    <br />
                    activated.
                  </p>
                  <div className="benefit-card-popup-buttons benefit-card-popup-buttons-success">
                    <button
                      className="benefit-card-popup-ok"
                      onClick={handleCloseSuccessPopup}
                    >
                      Okay
                    </button>
                  </div>
                </>
              ) : isErrorMode ? (
                <>
                  <h3 className="benefit-card-popup-title" style={{ color: "#FF6B6B" }}>Oops!</h3>
                  <p className="benefit-card-popup-message">
                    {errorMessage}
                  </p>
                  <div className="benefit-card-popup-buttons benefit-card-popup-buttons-success">
                    <button
                      className="benefit-card-popup-ok"
                      onClick={handleCloseErrorPopup}
                    >
                      Okay
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="benefit-card-popup-title">Use Privilege</h3>
                  <p className="benefit-card-popup-message">
                    You are about to use this privilege.
                    <br />
                    Please confirm if you wish to proceed.
                  </p>
                  <div className="benefit-card-popup-buttons">
                    <button
                      className="benefit-card-popup-cancel"
                      onClick={handleCancel}
                      disabled={isCreating}
                    >
                      No, Cancel
                    </button>
                    <button
                      key={isCreating ? 'creating' : 'idle'} 
                      className="benefit-card-popup-confirm"
                      onClick={handleUsePrivilege}
                      disabled={isCreating}
                    >
                      {isCreating ? "Sending..." : "Yes, confirm"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default memo(BenefitCard);
